/*
 * /classes/:slug/contributions — hộp góp ý của học viên: xem feedback đã gửi + tạo feedback mới.
 * API: GET /api/classes/:slug/contributions → { contributions: [{ id, category, title, details, status, created_at }] }
 *      POST { event_id, category, title, details } → { badge_awarded? }
 */
import { useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, LoadingState, Page, PageHeader, StatusNote, usePageTitle } from "@/components/page"
import { useClass } from "@/app/ClassLayout"
import { api, post } from "@/lib/api"
import { formatDisplayDate } from "@/lib/format"
import { contributionCategories } from "./community/shared"

const categoryItems = Object.entries(contributionCategories).map(([value, label]) => ({ value, label }))

function ContributionForm({ busy, onCancel, onSubmit }: { busy: boolean; onCancel: () => void; onSubmit: (v: { category: string; title: string; details: string }) => void }) {
  const [category, setCategory] = useState("feature_request")
  const [title, setTitle] = useState("")
  const [details, setDetails] = useState("")
  const ok = title.trim().length >= 5 && details.trim().length >= 10
  return (
    <Card>
      <CardContent>
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault()
            if (ok && !busy) onSubmit({ category, title, details })
          }}
        >
          <div className="flex flex-col gap-2">
            <Label>Loại feedback</Label>
            <Select items={categoryItems} value={category} onValueChange={(v) => setCategory(String(v))}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryItems.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fb-title">Tiêu đề</Label>
            <Input id="fb-title" maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Con muốn Simba thay đổi điều gì?" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="fb-details">Nội dung</Label>
            <Textarea id="fb-details" rows={6} maxLength={2000} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Nói ngắn gọn điều đang xảy ra và điều con mong muốn." />
            <p className="text-sm text-muted-foreground">Tiêu đề từ 5 ký tự, nội dung từ 10 ký tự. {details.length}/2000</p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={onCancel}>Hủy</Button>
            <Button type="submit" disabled={busy || !ok}>
              <Icon name="send" /> {busy ? "Đang gửi…" : "Gửi feedback"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function ContributionItem({ item }: { item: any }) {
  return (
    <li>
      <Card size="sm">
        <CardContent className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>{contributionCategories[item.category] || "Góp ý"}</Badge>
            <Badge variant={item.status === "received" ? "success" : "warning"}>{item.status === "received" ? "Đã tiếp nhận" : item.status}</Badge>
            <time className="ml-auto text-sm text-muted-foreground">{formatDisplayDate(item.created_at)}</time>
          </div>
          <h3 className="font-heading text-base font-semibold break-words text-ink">{item.title}</h3>
          <p className="text-sm break-words whitespace-pre-line text-muted-foreground">{item.details}</p>
        </CardContent>
      </Card>
    </li>
  )
}

export default function ContributionsPage() {
  usePageTitle("Góp ý", "Gửi feedback giúp Simba tốt hơn.")
  const { slug } = useClass()
  const [data, setData] = useState<any>()
  const [error, setError] = useState("")
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ tone: "success" | "error"; text: string } | null>(null)

  const path = `/api/classes/${encodeURIComponent(slug)}/contributions`
  const load = () => api(path).then(setData)
  const firstLoad = () => {
    setError("")
    load().catch((e: any) => setError(e.message))
  }
  useEffect(() => { firstLoad() }, [slug])

  const submit = async (v: { category: string; title: string; details: string }) => {
    setBusy(true)
    setNote(null)
    try {
      const result: any = await post(path, { event_id: crypto.randomUUID(), ...v })
      setCreating(false)
      await load()
      setNote({ tone: "success", text: result?.badge_awarded ? `Con đã nhận badge “${result.badge_awarded}”!` : "Cảm ơn con. Góp ý đã được ghi nhận để Simba tiếp tục tốt hơn." })
    } catch (e: any) {
      setNote({ tone: "error", text: e.message })
    } finally {
      setBusy(false)
    }
  }

  if (error) return <Page><ErrorState message={error} onRetry={firstLoad} /></Page>
  if (!data) return <LoadingState label="Đang mở hộp góp ý…" />
  const list: any[] = data.contributions || []

  return (
    <Page className="max-w-[820px]">
      <PageHeader
        eyebrow="Góp ý"
        title={creating ? "Tạo feedback mới" : "Feedback của con"}
        description={creating ? "Mỗi góp ý giúp Simba hiểu con hơn." : "Thấy gì chưa ổn, hay có ý tưởng hay? Kể cho Simba nhé."}
        actions={!creating && (
          <Button onClick={() => { setCreating(true); setNote(null) }}>
            <Icon name="chat" /> Tạo feedback mới
          </Button>
        )}
      />
      {note && <StatusNote tone={note.tone}>{note.text}</StatusNote>}
      {creating ? (
        <ContributionForm busy={busy} onCancel={() => setCreating(false)} onSubmit={submit} />
      ) : list.length ? (
        <ul className="flex flex-col gap-3" aria-label="Feedback đã gửi">
          {list.map((item) => <ContributionItem key={item.id} item={item} />)}
        </ul>
      ) : (
        <EmptyState mascot="giraffe-thinking" title="Con chưa gửi feedback nào" description="Góp ý đầu tiên có thể mở một badge bất ngờ đấy." action={<Button onClick={() => setCreating(true)}><Icon name="chat" /> Gửi góp ý đầu tiên</Button>} />
      )}
    </Page>
  )
}
